// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'redux-pender';
import * as LettersAPI from 'lib/api/letters';

const GET_RECENT_LETTERS = 'lettering/GET_RECENT_LETTERS';
const PREFETCH_RECENT_LETTERS = 'lettering/PREFETCH';
const REVEAL_PREFETCHED = 'lettering/REVEAL_PREFETCHED';
const CLEAR_USER_LETTERS = 'lettering/CLEAR_USER_LETTERS';
const GET_USER_LETTERS = 'lettering/GET_USER_LETTERS';
const PREFETCH_USER_LETTERS = 'lettering/PREFETCH_USER_LETTERS';
const GET_TRENDING_LETTERS = 'lettering/GET_TRENDING_LETTERS';
const PREFETCH_TRENDING_LETTERS = 'lettering/PREFETCH_TRENDING_LETTERS';
const GET_TAG_LETTERS = 'lettering/GET_TAG_LETTERS';
const PREFETCH_TAG_LETTERS = 'lettering/PREFETCH_TAG_LETTERS';
const GET_CATEGORY_LETTERS = 'lettering/GET_CATEGORY_LETTERS';
const PREFETCH_CATEGORY_LETTERS = 'lettering/PREFETCH_CATEGORY_LETTERS';
const CLEAR_CATEGORY_LETTERS = 'lettering/CLEAR_CATEGORY_LETTERS';
const CLEAR_TAG_LETTERS = 'lettering/CLEAR_TAG_LETTERS';
const GET_TEMP_LETTERS = 'lettering/GET_TEMP_LETTERS';
const PREFETCH_TEMP_LETTERS = 'lettering/PREFETCH_TEMP_LETTERS';
const REMOVE_TEMP_LETTER = 'lettering/REMOVE_TEMP_LETTER';
const FLUSH_LIST = 'lettering/FLUSH_LIST';

// potential improvement :: remove duplicates
export const actionCreators = {
  getRecentLetters: createAction(GET_RECENT_LETTERS, LettersAPI.getPublicLetters),
  prefetchRecentLetters: createAction(PREFETCH_RECENT_LETTERS, LettersAPI.getPublicLetters),
  revealPrefetched: createAction(REVEAL_PREFETCHED, (type: string) => type),
  clearUserLetters: createAction(CLEAR_USER_LETTERS),
  getUserLetters: createAction(
    GET_USER_LETTERS,
    LettersAPI.getUserLetters,
    (meta: { username: string, tag?: string }) => meta,
  ),
  prefetchUserLetters: createAction(PREFETCH_USER_LETTERS, LettersAPI.getUserLetters),
  getTrendingLetters: createAction(GET_TRENDING_LETTERS, LettersAPI.getTrendingLetters),
  prefetchTrendingLetters: createAction(PREFETCH_TRENDING_LETTERS, LettersAPI.getTrendingLetters),
  getTagLetters: createAction(
    GET_TAG_LETTERS,
    LettersAPI.getPublicLettersByTag,
    (meta: { tag: string }) => meta,
  ),
  prefetchTagLetters: createAction(PREFETCH_TAG_LETTERS, LettersAPI.getPublicLettersByTag),
  getCategoryLetters: createAction(
    GET_CATEGORY_LETTERS,
    LettersAPI.getPublicLettersByCategory,
    (meta: { category: string, sort?: string }) => meta,
  ),
  prefetchCategoryLetters: createAction(
    PREFETCH_CATEGORY_LETTERS,
    LettersAPI.getPublicLettersByCategory,
  ),
  clearCategoryLetters: createAction(CLEAR_CATEGORY_LETTERS),
  clearTagLetters: createAction(CLEAR_TAG_LETTERS),
  getTempLetters: createAction(GET_TEMP_LETTERS, LettersAPI.getTempLetters),
  prefetchTempLetters: createAction(PREFETCH_TEMP_LETTERS, LettersAPI.getTempLetters),
  removeTempLetter: createAction(REMOVE_TEMP_LETTER, (letterId: string) => letterId),
  flushList: createAction(FLUSH_LIST),
};

export type LetterItem = {
  id: string,
  title: string,
  body: string,
  thumbnail: string,
  is_markdown: boolean,
  is_private: boolean,
  released_at: string,
  created_at: string,
  updated_at: string,
  tags: string[],
  categories: any[],
  url_slug: string,
  likes: number,
  comments_count: number,
  user: {
    id: string,
    username: string,
    lettering_name: string,
    short_bio: string,
    thumbnail: ?string,
  },
  meta: any,
};

export type LetteringSet = {
  letters: ?(LetterItem[]),
  prefetched: ?(LetterItem[]),
  end: boolean,
};

export type Lettering = {
  trendingMap: {
    [id: string]: boolean,
  },
  trending: LetteringSet,
  recent: LetteringSet,
  user: LetteringSet & { currentUsername: ?string, currentTag: ?string },
  tag: LetteringSet & { currentTag: ?string },
  category: LetteringSet & { currentCategory: ?string },
  temp: LetteringSet,
};

type RevealPrefetchedAction = ActionType<typeof actionCreators.revealPrefetched>;
type LettersResponseAction = {
  type: string,
  payload: {
    data: LetterItem[],
    category?: ?string,
    sort?: ?string,
  },
  meta: any,
};
type RemoveTempLetterAction = ActionType<typeof actionCreators.removeTempLetter>;

export interface LetteringActionCreators {
  getRecentLetters(cursor: string, category: string, sort: string): any;

  prefetchRecentLetters(cursor: string, category: string, sort: string): any;

  revealPrefetched(type: string): any;

  getUserLetters(payload: LettersAPI.GetUserLettersPayload): any;

  prefetchUserLetters(payload: LettersAPI.GetUserLettersPayload): any;

  getTrendingLetters(): any;

  prefetchTrendingLetters(offset: number): any;

  getTagLetters(payload: LettersAPI.GetPublicLettersByTagPayload): any;

  prefetchTagLetters(payload: LettersAPI.GetPublicLettersByTagPayload): any;

  getCategoryLetters(payload: LettersAPI.GetPublicLettersByCategoryPayload): any;

  prefetchCategoryLetters(payload: LettersAPI.GetPublicLettersByCategoryPayload): any;

  getTempLetters(payload: LettersAPI.GetTempLettersPayload): any;

  prefetchTempLetters(payload: LettersAPI.GetTempLettersPayload): any;

  clearCategoryLetters(): any;

  clearTagLetters(): any;

  clearUserLetters(): any;

  removeTempLetter(letterId: string): any;

  flushList(): any;
}

const initialLetteringSet = {
  letters: null,
  prefetched: null,
  end: false,
};

const initialState: Lettering = {
  trendingMap: {},
  trending: initialLetteringSet,
  recent: initialLetteringSet,
  user: { ...initialLetteringSet, currentUsername: null, currentTag: null },
  tag: { ...initialLetteringSet, currentTag: null },
  category: { ...initialLetteringSet, currentCategory: null },
  temp: initialLetteringSet,
};

const reducer = handleActions(
  {
    [REVEAL_PREFETCHED]: (state, action: RevealPrefetchedAction) => {
      return produce(state, draft => {
        const { payload } = action;
        const { letters, prefetched } = draft[payload];
        if (letters && prefetched) {
          letters.push(...prefetched);
          draft[payload].prefetched = null;
        }
      });
    },
    [CLEAR_USER_LETTERS]: state => {
      return produce(state, draft => {
        draft.user = {
          letters: null,
          prefetched: null,
          end: false,
          currentUsername: null,
          currentTag: null,
        };
      });
    },
    [CLEAR_TAG_LETTERS]: state => {
      return produce(state, draft => {
        draft.tag = {
          letters: null,
          prefetched: null,
          end: false,
          currentTag: null,
        };
      });
    },
    [CLEAR_CATEGORY_LETTERS]: state => {
      return produce(state, draft => {
        draft.category = {
          letters: null,
          prefetched: null,
          end: false,
          currentCategory: null,
        };
      });
    },
    [REMOVE_TEMP_LETTER]: (state, { payload }: RemoveTempLetterAction) => {
      return produce(state, draft => {
        if (!draft.temp.letters) return;
        draft.temp.letters = draft.temp.letters.filter(p => p.id !== payload);
      });
    },
    [FLUSH_LIST]: state => {
      return initialState;
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_RECENT_LETTERS,
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      const { category, sort } = action.payload;
      return produce(state, draft => {
        draft.recent = {
          category,
          sort,
          end: false,
          letters: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_RECENT_LETTERS,
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.recent.prefetched = data;
        if (data && data.length === 0) {
          draft.recent.end = true;
        }
      });
    },
  },
  {
    type: GET_TAG_LETTERS,
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      return produce(state, draft => {
        draft.tag = {
          end: action.payload.data.length < 20,
          letters: action.payload.data,
          prefetched: null,
          currentTag: action.meta.tag,
        };
      });
    },
  },
  {
    type: PREFETCH_TAG_LETTERS,
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.tag.prefetched = data;
        if (data && data.length === 0) {
          draft.tag.end = true;
        }
      });
    },
  },
  {
    type: GET_CATEGORY_LETTERS,
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      return produce(state, draft => {
        draft.category = {
          end: action.payload.data.length < 20,
          letters: action.payload.data,
          prefetched: null,
          currentCategory: action.meta.category,
          sort: action.meta.sort,
        };
      });
    },
  },
  {
    type: PREFETCH_CATEGORY_LETTERS,
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.category.prefetched = data;
        if (data && data.length === 0) {
          draft.category.end = true;
        }
      });
    },
  },
  {
    type: GET_TRENDING_LETTERS,
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      return produce(state, draft => {
        action.payload.data.forEach(letter => {
          draft.trendingMap[letter.id] = true;
        });
        draft.trending = {
          end: false,
          letters: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_TRENDING_LETTERS,
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        const filtered = data.filter(letter => !state.trendingMap[letter.id]);
        draft.trending.prefetched = filtered;
        filtered.forEach(letter => {
          draft.trendingMap[letter.id] = true;
        });
        if (data && data.length === 0) {
          draft.trending.end = true;
        }
      });
    },
  },
  {
    type: GET_USER_LETTERS,
    onPending: (state: Lettering) => {
      return produce(state, draft => {
        draft.user.end = false;
        draft.user.prefetched = null;
      });
    },
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      return produce(state, draft => {
        draft.user.letters = action.payload.data;
        draft.user.currentUsername = action.meta.username;
        if (action.meta.tag) {
          draft.user.currentTag = action.meta.tag;
        }
        if (action.payload.data.length < 20) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_USER_LETTERS,
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.user.prefetched = data;
        if (data && data.length === 0) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: GET_TEMP_LETTERS,
    onPending: (state: Lettering) => {
      return produce(state, draft => {
        draft.temp.end = false;
        draft.temp.prefetched = null;
      });
    },
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      return produce(state, draft => {
        draft.temp.letters = action.payload.data;
        if (action.payload.data.length < 20) {
          draft.temp.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_TEMP_LETTERS,
    onSuccess: (state: Lettering, action: LettersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.temp.prefetched = data;
        if (data && data.length === 0) {
          draft.temp.end = true;
        }
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/lettering.js